import {
  type InfiniteData,
  partialMatchKey,
  type PlaceholderDataFunction,
  type QueryClient,
  type QueryKey,
  useQueryClient,
} from '@tanstack/react-query'
import { useCallback, useMemo, useSyncExternalStore } from 'react'
import { z } from 'zod'
import {
  getRepositoryBranchesKeyPrefix,
  useCurrentUser,
  useGetRepository,
  useGetRepositoryBranches,
} from '../../utils/api-client/api-hooks'
import {
  type BranchEmbedParameter,
  type BranchPaginatedResponse,
  type BranchResponse,
  type Tool,
} from '../../utils/api-client/api-schemas'

/** How many selected branches are remembered per user and repository, across tools. */
export const RECENTLY_VISITED_LIMIT = 10

/** How many of the remembered branches the "Recently visited" section shows. */
export const RECENTLY_VISITED_SHOWN = 3

/**
 * Keyed by username as well as repository: people sign in to several accounts in one browser to try
 * Pixee, and one account's recently visited branches should not show up for another.
 */
export const getRecentlyVisitedStorageKey = ({ username, repositoryId }: { username: string; repositoryId: string }) =>
  `branch-selector/recently-visited/${encodeURIComponent(username)}/${repositoryId}`

const RecentlyVisitedBranchesSchema = z.array(z.string())

/** Parses the stored list, treating anything malformed as nothing remembered. */
export const parseRecentlyVisitedBranches = (raw: string | null): string[] => {
  if (!raw) {
    return []
  }
  try {
    return RecentlyVisitedBranchesSchema.safeParse(JSON.parse(raw)).data?.slice(0, RECENTLY_VISITED_LIMIT) ?? []
  } catch {
    return []
  }
}

/** Moves `branch` to the front of the most-recent-first list, capped at {@link RECENTLY_VISITED_LIMIT}. */
export const addRecentlyVisitedBranch = (recentlyVisited: readonly string[], branch: string): string[] =>
  [branch, ...recentlyVisited.filter(name => name !== branch)].slice(0, RECENTLY_VISITED_LIMIT)

const NOTHING_REMEMBERED: readonly string[] = []

// Module-level on purpose: the page and the selector both read the list, so every reader subscribes
// to one store, and a visit recorded by the selector reaches the page's branch lookup in the same
// render. Nothing resets it between tests, so each test stores under a repository no other test uses.
const listeners = new Set<() => void>()
const parsedByKey = new Map<string, { raw: string | null; parsed: readonly string[] }>()

const notifyListeners = () => listeners.forEach(listener => listener())

const subscribe = (listener: () => void) => {
  if (listeners.size === 0) {
    // Another tab recording a visit changes `localStorage` without calling into this module.
    // eslint-disable-next-line no-restricted-properties -- `useSyncExternalStore` calls the unsubscribe below
    window.addEventListener('storage', notifyListeners)
  }
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) {
      // eslint-disable-next-line no-restricted-properties -- the cleanup for the subscription above
      window.removeEventListener('storage', notifyListeners)
    }
  }
}

// Storage can throw (private browsing, quota, disabled site data). Remembering branches is a
// convenience, so a failure degrades to remembering them in memory until the page reloads.
const inMemoryFallback = new Map<string, string>()

const readRaw = (key: string) => {
  // Checked first: when only `setItem` throws (a full quota, Safari private browsing), `getItem` still
  // works but returns the list from before the visit the fallback holds.
  const fallback = inMemoryFallback.get(key)
  if (fallback !== undefined) {
    return fallback
  }
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

/** The parsed list, the same array for as long as the stored string is unchanged, as `useSyncExternalStore` needs. */
const readRecentlyVisitedBranches = (key: string): readonly string[] => {
  const raw = readRaw(key)
  const cached = parsedByKey.get(key)
  if (cached && cached.raw === raw) {
    return cached.parsed
  }
  const parsed = parseRecentlyVisitedBranches(raw)
  parsedByKey.set(key, { raw, parsed })
  return parsed
}

const writeRecentlyVisitedBranches = (key: string, recentlyVisited: readonly string[]) => {
  const raw = JSON.stringify(recentlyVisited)
  try {
    localStorage.setItem(key, raw)
    inMemoryFallback.delete(key)
  } catch {
    // See `readRaw`.
    inMemoryFallback.set(key, raw)
  }
  notifyListeners()
}

/**
 * The branches the signed-in user last selected on this repository, most recent first, remembered in
 * this browser. Empty until the current user is known, since the list is stored per user.
 */
export const useRecentlyVisitedBranches = ({ repositoryId }: { repositoryId: string }) => {
  const { data: currentUser, isLoading } = useCurrentUser()
  const username = currentUser?.username ?? currentUser?.login
  const key = username ? getRecentlyVisitedStorageKey({ username, repositoryId }) : undefined

  const recentlyVisited = useSyncExternalStore(subscribe, () =>
    key ? readRecentlyVisitedBranches(key) : NOTHING_REMEMBERED
  )

  const recordVisit = useCallback(
    (branch: string) => {
      if (key) {
        writeRecentlyVisitedBranches(key, addRecentlyVisitedBranch(readRecentlyVisitedBranches(key), branch))
      }
    },
    [key]
  )

  /** `isLoading` is true until the current user, and so which list to read, is known. */
  return { recentlyVisited, recordVisit, isLoading }
}

export type BranchSections = {
  defaultBranch?: BranchResponse
  recentBranches: BranchResponse[]
  otherBranches: BranchResponse[]
}

/**
 * Splits the branches into the selector's three sections.
 *
 * - Default: the default branch, when the tool has scanned it.
 * - Recently visited: the first {@link RECENTLY_VISITED_SHOWN} remembered branches the tool has
 *   scanned, in visit order. The default branch is left out, since it already has its own section.
 * - Other branches: everything else, in the order given (newest import first).
 *
 * @param knownBranches the default and remembered branches, as the branches endpoint returned them for the tool
 * @param recentlyVisited remembered branch names, most recent first
 * @param branches the tool's branches loaded so far, newest import first
 */
export const buildBranchSections = ({
  knownBranches,
  recentlyVisited,
  branches,
}: {
  knownBranches: readonly BranchResponse[]
  recentlyVisited: readonly string[]
  branches: readonly BranchResponse[]
}): BranchSections => {
  const defaultBranch = knownBranches.find(branch => branch.is_default) ?? branches.find(branch => branch.is_default)
  const knownByName = new Map(knownBranches.map(branch => [branch.name, branch]))
  const recentBranches = recentlyVisited
    .map(name => knownByName.get(name))
    .filter((branch): branch is BranchResponse => !!branch && !branch.is_default)
    .slice(0, RECENTLY_VISITED_SHOWN)
  const shownRecentNames = new Set(recentBranches.map(branch => branch.name))
  const otherBranches = branches.filter(branch => !branch.is_default && !shownRecentNames.has(branch.name))

  return { defaultBranch, recentBranches, otherBranches }
}

/**
 * The branch the page should show for the selected tool.
 *
 * Keeps the branch in the URL when the tool has scanned it. Otherwise falls back to the default
 * branch when the tool has scanned that, and otherwise to the branch with the tool's most recently
 * imported scan. That fallback is the first-load rule, what happens on switching to a tool that has
 * not scanned the current branch, and what happens when no tool has scanned the URL's branch (see
 * {@link getToolToFollow} for when another tool has). `undefined` when the tool has no scans on any
 * branch.
 *
 * @param urlBranch the branch named in the URL, if any
 * @param knownBranches the tool's branches among the default, recently visited, and URL branches
 * @param latestBranch the tool's branch with the most recently imported scan
 */
export const resolveBranch = ({
  urlBranch,
  knownBranches,
  latestBranch,
}: {
  urlBranch?: string
  knownBranches: readonly BranchResponse[]
  latestBranch?: BranchResponse
}): string | undefined => {
  if (urlBranch && knownBranches.some(branch => branch.name === urlBranch)) {
    return urlBranch
  }
  return knownBranches.find(branch => branch.is_default)?.name ?? latestBranch?.name
}

/**
 * The tool to switch to so the page can show the URL's branch, when the selected tool never scanned it.
 *
 * A branch link without `?tool=` opens on the tool of the repository's latest scan, which may never
 * have scanned that branch. Rather than open some other branch, the tool follows the branch: it
 * becomes the tool with the most recent scan on it. `undefined` when the selected tool has scanned
 * the branch, or when no tool the tool selector offers has (then {@link resolveBranch} applies the
 * first-load rule). A tool the selector does not offer is never followed: the page could not show
 * it as selected, just as the first-load tool must be one of the offered tools.
 *
 * @param urlBranch the branch named in the URL, if any
 * @param selectedTool the tool in the tool selector
 * @param knownBranches the selected tool's branches among the default, recently visited, and URL branches
 * @param urlBranchFromAnyTool the URL's branch as listed for every tool, with its latest scan embedded
 * @param availableTools the tools the tool selector offers
 */
export const getToolToFollow = ({
  urlBranch,
  selectedTool,
  knownBranches,
  urlBranchFromAnyTool,
  availableTools,
}: {
  urlBranch?: string
  selectedTool: Tool
  knownBranches: readonly BranchResponse[]
  urlBranchFromAnyTool?: BranchResponse
  availableTools: ReadonlySet<Tool>
}): Tool | undefined => {
  if (!urlBranch || knownBranches.some(branch => branch.name === urlBranch)) {
    return undefined
  }
  const latestTool = urlBranchFromAnyTool?._embedded?.['latest-scan']?.detector
  return latestTool && latestTool !== selectedTool && availableTools.has(latestTool) ? latestTool : undefined
}

/** Ask the branches endpoint to inline each branch's most recently imported scan. */
export const LATEST_SCAN_EMBED: readonly BranchEmbedParameter[] = ['latest-scan']

/**
 * `placeholderData` that keeps showing the previous results while a new request for the same
 * repository and tool loads (another search, another set of names), but not across a tool or
 * repository switch: those branches are not this list's, and showing them would offer branches the
 * new tool, or the new repository, never scanned.
 */
export const keepPreviousBranchesOf =
  ({ branchesHref, tool }: { branchesHref: string | undefined; tool: Tool | undefined }) =>
  <TData, TError, TQueryKey extends QueryKey>(
    ...[previousData, previousQuery]: Parameters<PlaceholderDataFunction<TData, TError, TData, TQueryKey>>
  ): TData | undefined =>
    previousQuery && partialMatchKey(previousQuery.queryKey, getRepositoryBranchesKeyPrefix({ branchesHref, tool }))
      ? previousData
      : undefined

const findCachedRepositoryBranch = (
  client: QueryClient,
  { branchesHref, tool, name }: { branchesHref: string; tool: Tool; name: string }
) =>
  client
    .getQueriesData<BranchPaginatedResponse | InfiniteData<BranchPaginatedResponse>>({
      queryKey: getRepositoryBranchesKeyPrefix({ branchesHref, tool }),
    })
    .flatMap(([, data]) => {
      if (!data) {
        return []
      }
      return 'pages' in data ? data.pages : [data]
    })
    .flatMap(page => page._embedded.items)
    .find(branch => branch.name === name)

/**
 * `name` among the branches already fetched for `tool`, by any branches query, kept current as those
 * queries fill in.
 *
 * Picking a branch from the selector puts it in the URL before a query validating it can answer.
 * The branch came from one of these lists, so finding it here lets the page apply it at once instead
 * of waiting a round trip to confirm what it already knows. Subscribed to the query cache rather than
 * read once during render, so the answer follows the cache instead of whatever it held at that render.
 */
export const useCachedRepositoryBranch = ({
  branchesHref,
  tool,
  name,
}: {
  branchesHref?: string
  tool?: Tool
  name?: string
}) => {
  const queryClient = useQueryClient()
  const subscribeToCache = useCallback(
    (onChange: () => void) => queryClient.getQueryCache().subscribe(onChange),
    [queryClient]
  )
  // The snapshot is a branch object out of cached data, so it keeps its identity until that data changes.
  return useSyncExternalStore(subscribeToCache, () =>
    branchesHref && tool && name ? findCachedRepositoryBranch(queryClient, { branchesHref, tool, name }) : undefined
  )
}

/**
 * The tool's branches among the default branch, the recently visited branches, and the URL's branch,
 * looked up by exact name with their latest scans embedded.
 *
 * One request answers the selector's Default and Recently visited sections and the page's first-load
 * rule, so none of them depends on landing on the first page of the tool's branches. The page and the
 * selector both call this with the same inputs, and React Query sends the request once for both.
 */
export const useGetKnownRepositoryBranches = ({
  repositoryId,
  tool,
  urlBranch,
}: {
  repositoryId: string
  tool?: Tool
  urlBranch?: string
}) => {
  const { data: repository } = useGetRepository({ repositoryId })
  const { recentlyVisited, isLoading: isRecentlyVisitedLoading } = useRecentlyVisitedBranches({ repositoryId })
  const branchesHref = repository?._links.branches?.href
  const defaultBranchName = repository?.default_branch
  const knownNames = useMemo(
    () => [...new Set([defaultBranchName, ...recentlyVisited, urlBranch].filter(name => name !== undefined))],
    [defaultBranchName, recentlyVisited, urlBranch]
  )

  const known = useGetRepositoryBranches({
    branchesHref,
    tool,
    names: knownNames,
    embed: LATEST_SCAN_EMBED,
    page: { number: 0, size: Math.max(knownNames.length, 1) },
    enabled: !!tool && !isRecentlyVisitedLoading && knownNames.length > 0,
    // Picking a branch adds it to the recently visited names, which is a new request; the selector
    // keeps showing the previous sections meanwhile instead of emptying.
    placeholderData: keepPreviousBranchesOf({ branchesHref, tool }),
  })

  return {
    knownNames,
    /** This request's own answer: `undefined` while it loads, even while the previous one is shown. */
    knownBranches: known.isPlaceholderData ? undefined : known.data?._embedded.items,
    /**
     * What the selector's Default and Recently visited sections are built from: the previous answer
     * while a new one loads, and empty when there is nothing to look up or the lookup failed (the
     * sections then come from the paged list alone).
     */
    sectionBranches: knownNames.length === 0 || known.isError ? [] : known.data?._embedded.items,
    isError: known.isError,
    isRecentlyVisitedLoading,
  }
}
